import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardStats, GrowthTrends } from '../../types/dashboard';
import MetricCard from './MetricCard';
import GrowthChart from './GrowthChart';
import RevenueChart from './RevenueChart';

const Dashboard: React.FC = () => {
  const { admin } = useAuth();
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Individual loading states for each metric category
  const [loadingStates, setLoadingStates] = useState({
    users: true,
    premium: true,
    financial: true,
    coins: true,
    engagement: true,
    content: true,
    growth: true,
    monthlyFinancials: true
  });

  // Partial stats object - each metric loads independently
  const [stats, setStats] = useState<Partial<DashboardStats>>({});
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const getToken = useCallback(() => localStorage.getItem('admin_token'), []);
  const queryIncludeDeleted = includeDeleted ? '?include_deleted=1' : '';

  // Individual fetch functions for each metric category
  const fetchUserMetrics = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, users: true }));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/dashboard/metrics/users${queryIncludeDeleted}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.data.success) {
        setStats(prev => ({ ...prev, users: response.data.data }));
      }
    } catch (error: any) {
      console.error('Error fetching user metrics:', error);
      setStats(prev => ({ 
        ...prev, 
        users: { 
          total: 0, 
          active: 0, 
          verified: 0, 
          new_today: 0, 
          new_7d: 0, 
          new_30d: 0, 
          banned: 0, 
          gender_breakdown: {},
          error: error.response?.data?.message || 'Failed to fetch user metrics' 
        } 
      }));
    } finally {
      setLoadingStates(prev => ({ ...prev, users: false }));
    }
  }, [getToken, queryIncludeDeleted]);

  const fetchPremiumMetrics = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, premium: true }));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/dashboard/metrics/premium${queryIncludeDeleted}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.data.success) {
        setStats(prev => ({ ...prev, premium: response.data.data }));
      }
    } catch (error: any) {
      console.error('Error fetching premium metrics:', error);
      setStats(prev => ({ 
        ...prev, 
        premium: { 
          total_premium: 0,
          active_subscriptions: 0,
          expired_subscriptions: 0,
          mrr: 0,
          total_revenue: 0,
          revenue_by_source: [],
          avg_subscription_value: 0,
          error: error.response?.data?.message || 'Failed to fetch premium metrics' 
        } 
      }));
    } finally {
      setLoadingStates(prev => ({ ...prev, premium: false }));
    }
  }, [getToken, queryIncludeDeleted]);

  const fetchFinancialMetrics = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, financial: true }));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/dashboard/metrics/financial${queryIncludeDeleted}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.data.success) {
        setStats(prev => ({ ...prev, financial: response.data.data }));
      }
    } catch (error: any) {
      console.error('Error fetching financial metrics:', error);
      setStats(prev => ({ 
        ...prev, 
          financial: { 
          revenue: 0,
          costBreakdown: [],
          revenueBySource: [],
          error: error.response?.data?.message || 'Failed to fetch financial metrics' 
        } 
      }));
    } finally {
      setLoadingStates(prev => ({ ...prev, financial: false }));
    }
  }, [getToken, queryIncludeDeleted]);

  const fetchCoinMetrics = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, coins: true }));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/dashboard/metrics/coins${queryIncludeDeleted}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.data.success) {
        setStats(prev => ({ ...prev, coins: response.data.data }));
      }
    } catch (error: any) {
      console.error('Error fetching coin metrics:', error);
      setStats(prev => ({ 
        ...prev, 
        coins: { 
          total_issued: 0,
          total_used: 0,
          remaining: 0,
          avg_per_user: 0,
          top_earners: [],
          error: error.response?.data?.message || 'Failed to fetch coin metrics' 
        } 
      }));
    } finally {
      setLoadingStates(prev => ({ ...prev, coins: false }));
    }
  }, [getToken, queryIncludeDeleted]);

  const fetchEngagementMetrics = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, engagement: true }));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/dashboard/metrics/engagement${queryIncludeDeleted}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.data.success) {
        setStats(prev => ({ ...prev, engagement: response.data.data }));
      }
    } catch (error: any) {
      console.error('Error fetching engagement metrics:', error);
      setStats(prev => ({ 
        ...prev, 
        engagement: { 
          total_conversations: 0,
          active_conversations: 0,
          messages_today: 0,
          messages_7d: 0,
          messages_30d: 0,
          avg_messages_per_conversation: 0,
          albums_created: 0,
          error: error.response?.data?.message || 'Failed to fetch engagement metrics' 
        } 
      }));
    } finally {
      setLoadingStates(prev => ({ ...prev, engagement: false }));
    }
  }, [getToken, queryIncludeDeleted]);

  const fetchContentMetrics = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, content: true }));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/dashboard/metrics/content${queryIncludeDeleted}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.data.success) {
        setStats(prev => ({ ...prev, content: response.data.data }));
      }
    } catch (error: any) {
      console.error('Error fetching content metrics:', error);
      setStats(prev => ({ 
        ...prev, 
        content: { 
          total_photos: 0,
          total_videos: 0,
          total_albums: 0,
          s3_storage_gb: 0,
          s3_object_count: 0,
          error: error.response?.data?.message || 'Failed to fetch content metrics' 
        } 
      }));
    } finally {
      setLoadingStates(prev => ({ ...prev, content: false }));
    }
  }, [getToken, queryIncludeDeleted]);

  const fetchGrowthTrends = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, growth: true }));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/dashboard/metrics/growth${queryIncludeDeleted}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.data.success) {
        setStats(prev => ({ ...prev, growth: { data: response.data.data, error: undefined } }));
      }
    } catch (error: any) {
      console.error('Error fetching growth trends:', error);
      setStats(prev => ({ 
        ...prev, 
        growth: { 
          data: [],
          error: error.response?.data?.message || 'Failed to fetch growth trends' 
        } 
      }));
    } finally {
      setLoadingStates(prev => ({ ...prev, growth: false }));
    }
  }, [getToken, queryIncludeDeleted]);

  const fetchMonthlyFinancials = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, monthlyFinancials: true }));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/dashboard/metrics/monthly-financials${queryIncludeDeleted}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.data.success) {
        setStats(prev => ({ ...prev, monthlyFinancials: response.data.data }));
      }
    } catch (error: any) {
      console.error('Error fetching monthly financials:', error);
      setStats(prev => ({ 
        ...prev, 
        monthlyFinancials: [] 
      }));
    } finally {
      setLoadingStates(prev => ({ ...prev, monthlyFinancials: false }));
    }
  }, [getToken, queryIncludeDeleted]);

  // Refresh all metrics
  const refreshAll = useCallback(() => {
    setLastUpdated(new Date());
    // Fetch fast metrics first (database queries)
    fetchUserMetrics();
    fetchCoinMetrics();
    fetchEngagementMetrics();
    fetchContentMetrics();
    fetchPremiumMetrics();
    fetchGrowthTrends();
    
    // Fetch slow metrics last (AWS API calls)
    fetchFinancialMetrics();
    fetchMonthlyFinancials();
  }, [
    fetchUserMetrics,
    fetchCoinMetrics,
    fetchEngagementMetrics,
    fetchContentMetrics,
    fetchPremiumMetrics,
    fetchGrowthTrends,
    fetchFinancialMetrics,
    fetchMonthlyFinancials
  ]);

  // Initial load - optimized order
  useEffect(() => {
    fetchUserMetrics();
    fetchCoinMetrics();
    fetchEngagementMetrics();
    fetchContentMetrics();
    fetchPremiumMetrics();
    fetchGrowthTrends();
    fetchFinancialMetrics();
    fetchMonthlyFinancials();
    setLastUpdated(new Date());
  }, [
    fetchUserMetrics,
    fetchCoinMetrics,
    fetchEngagementMetrics,
    fetchContentMetrics,
    fetchPremiumMetrics,
    fetchGrowthTrends,
    fetchFinancialMetrics,
    fetchMonthlyFinancials
  ]);

  // Refetch when include-deleted filter changes (skip initial mount)
  const isFirstMount = React.useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    refreshAll();
  }, [includeDeleted, refreshAll]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(refreshAll, 60000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const growthData = stats.growth && 'data' in stats.growth ? (stats.growth as GrowthTrends).data : undefined;

  return (
    <div>
      {/* Page Header */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h4 className="mb-0 fw-semibold" style={{ color: '#495057', fontSize: '1.25rem' }}>Dashboard</h4>
              <p className="text-muted mb-0 small">Welcome back, {admin?.name || 'Admin'}</p>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <label className="d-flex align-items-center gap-1 small text-muted mb-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                />
                Include deleted content
              </label>
              {lastUpdated && (
                <small className="text-muted">Updated: {lastUpdated.toLocaleTimeString()}</small>
              )}
              <button className="btn btn-primary btn-sm" onClick={refreshAll}>
                <i className="bx bx-refresh me-1"></i> Refresh
              </button>
            </div>
          </div>
          {includeDeleted && (
            <small className="text-muted d-block mt-1">Counts include deleted users, albums, messages, and media.</small>
          )}
        </div>
      </div>

      {/* Financial */}
      <div className="row mb-3">
        <div className="col-12">
          <h6 className="mb-2 text-muted fw-semibold">Financial</h6>
        </div>
        <div className="col-xl-6 col-md-6 mb-3">
          <MetricCard
            icon="bx-dollar"
            label="Total Revenue"
            value={stats.financial?.revenue ? formatCurrency(stats.financial.revenue) : '₹0'}
            color="#28a745"
            loading={loadingStates.financial}
          />
        </div>
        <div className="col-xl-6 col-md-6 mb-3">
          <MetricCard
            icon="bx-coin-stack"
            label="Monthly Recurring Revenue"
            value={stats.premium?.mrr ? formatCurrency(stats.premium.mrr) : '₹0'}
            color="#006175"
            loading={loadingStates.premium}
          />
        </div>
      </div>

      {/* Key Metrics */}
      <div className="row mb-3">
        <div className="col-12">
          <h6 className="mb-2 text-muted fw-semibold">Key metrics</h6>
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-user"
            label="Total Users"
            value={stats.users?.total ? formatNumber(stats.users.total) : '0'}
            color="#006175"
            loading={loadingStates.users}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-crown"
            label="Premium Users"
            value={stats.premium?.total_premium ? formatNumber(stats.premium.total_premium) : '0'}
            color="#ffc107"
            loading={loadingStates.premium}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-coins"
            label="Total Coins"
            value={stats.coins?.remaining ? formatNumber(stats.coins.remaining) : '0'}
            color="#17a2b8"
            subtitle={stats.coins?.total_issued ? `Issued: ${formatNumber(stats.coins.total_issued)}` : ''}
            loading={loadingStates.coins}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-message"
            label="Active Conversations"
            value={stats.engagement?.active_conversations ? formatNumber(stats.engagement.active_conversations) : '0'}
            color="#007bff"
            loading={loadingStates.engagement}
          />
        </div>
      </div>

      {/* Charts */}
      <div className="row mb-3">
        <div className="col-lg-6 mb-3">
          <GrowthChart data={growthData} loading={loadingStates.growth} />
        </div>
        <div className="col-lg-6 mb-3">
          <RevenueChart data={stats.monthlyFinancials} loading={loadingStates.monthlyFinancials} formatCurrency={formatCurrency} />
        </div>
      </div>

      {/* User stats */}
      <div className="row mb-3">
        <div className="col-12">
          <h6 className="mb-2 text-muted fw-semibold">User stats</h6>
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-user-check"
            label="Active Now"
            value={stats.users?.active ? formatNumber(stats.users.active) : '0'}
            color="#28a745"
            loading={loadingStates.users}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-user-plus"
            label="New Today"
            value={stats.users?.new_today || 0}
            color="#006175"
            loading={loadingStates.users}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-badge-check"
            label="Verified Users"
            value={stats.users?.verified ? formatNumber(stats.users.verified) : '0'}
            color="#17a2b8"
            loading={loadingStates.users}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-block"
            label="Banned Users"
            value={stats.users?.banned ? formatNumber(stats.users.banned) : '0'}
            color="#dc3545"
            loading={loadingStates.users}
          />
        </div>
      </div>

      {/* Premium */}
      <div className="row mb-3">
        <div className="col-12">
          <h6 className="mb-2 text-muted fw-semibold">Premium</h6>
        </div>
        <div className="col-xl-4 col-md-6 mb-3">
          <MetricCard
            icon="bx-check-circle"
            label="Active Subscriptions"
            value={stats.premium?.active_subscriptions || 0}
            color="#28a745"
            loading={loadingStates.premium}
          />
        </div>
        <div className="col-xl-4 col-md-6 mb-3">
          <MetricCard
            icon="bx-x-circle"
            label="Expired Subscriptions"
            value={stats.premium?.expired_subscriptions || 0}
            color="#ffc107"
            loading={loadingStates.premium}
          />
        </div>
        <div className="col-xl-4 col-md-6 mb-3">
          <MetricCard
            icon="bx-dollar-circle"
            label="Avg Subscription Value"
            value={stats.premium?.avg_subscription_value ? formatCurrency(stats.premium.avg_subscription_value) : '₹0'}
            color="#006175"
            loading={loadingStates.premium}
          />
        </div>
      </div>

      {/* Content & Storage */}
      <div className="row mb-3">
        <div className="col-12">
          <h6 className="mb-2 text-muted fw-semibold">Content & storage</h6>
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-image"
            label="Total Photos"
            value={stats.content?.total_photos ? formatNumber(stats.content.total_photos) : '0'}
            color="#17a2b8"
            loading={loadingStates.content}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-video"
            label="Total Videos"
            value={stats.content?.total_videos ? formatNumber(stats.content.total_videos) : '0'}
            color="#dc3545"
            loading={loadingStates.content}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-folder"
            label="Total Albums"
            value={stats.content?.total_albums || 0}
            color="#6c757d"
            loading={loadingStates.content}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-cloud"
            label="S3 Storage"
            value={stats.content?.s3_storage_gb ? `${stats.content.s3_storage_gb} GB` : '0 GB'}
            color="#007bff"
            subtitle={stats.content?.s3_object_count ? `${stats.content.s3_object_count} objects` : ''}
            loading={loadingStates.content}
          />
        </div>
      </div>

      {/* Engagement */}
      <div className="row mb-3">
        <div className="col-12">
          <h6 className="mb-2 text-muted fw-semibold">Engagement</h6>
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-message-rounded"
            label="Messages Today"
            value={stats.engagement?.messages_today || 0}
            color="#006175"
            loading={loadingStates.engagement}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-message-dots"
            label="Messages (7d)"
            value={stats.engagement?.messages_7d || 0}
            color="#17a2b8"
            loading={loadingStates.engagement}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-chat"
            label="Total Conversations"
            value={stats.engagement?.total_conversations || 0}
            color="#28a745"
            loading={loadingStates.engagement}
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <MetricCard
            icon="bx-folder-plus"
            label="Albums Created"
            value={stats.engagement?.albums_created || 0}
            color="#ffc107"
            loading={loadingStates.engagement}
          />
        </div>
      </div>

      {/* Gender */}
      {stats.users?.gender_breakdown && Object.keys(stats.users.gender_breakdown).length > 0 && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body py-2">
                <h6 className="mb-2 text-muted fw-semibold">Gender</h6>
                <div className="row g-1">
                  {Object.entries(stats.users.gender_breakdown).map(([gender, count]) => (
                    <div key={gender} className="col-md-4 small">
                      <strong>{gender}:</strong> {formatNumber(count as number)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
